import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useLayoutEffect,
  useState,
} from "react"
import { useLocation, useNavigate, useSearchParams } from "react-router-dom"

import { User } from "@/api/resources/auth/types"

type AuthContextType = {
  isAuthenticated: boolean
  userInfo: User | null
  setUserInfo: (userInfo: User | null) => void
  isNcte?: boolean
}

// import { usePostHog } from "posthog-js/react"

import { setUser } from "@sentry/react"

import { API } from "@/api"
import { refreshToken } from "@/api/utils"
import FullPageLoading from "@/components/full-page-loading"
import { getAuthInfo, redirectToLogin, saveAuthInfo } from "@/lib/auth"
import { IS_LOCALHOST, PLATFORM } from "@/lib/constants"

const AuthContext = createContext<AuthContextType | undefined>(undefined)
const NcteOrganization = {
  slug: "national-council-for-teacher-education",
  id: "14",
}
export const AuthProvider = ({ children }: PropsWithChildren) => {
  const navigate = useNavigate()

  const [searchParams, setSearchParams] = useSearchParams()
  const [isNcte, setIsNcte] = useState(false)

  const [userInfo, setUserInfo] = useState<User | null>(
    getAuthInfo()?.user || null,
  )
  const isAuthenticated = !!userInfo
  // const isNcte = true

  const { pathname } = useLocation()
  const authCode = searchParams.get("code")

  const [isAuthenticating, setIsAuthenticating] = useState(true)

  // const posthog = usePostHog()

  useEffect(() => {
    if (!userInfo) return

    // posthog.identify(userInfo.uuid, {
    //   email: userInfo.email,
    //   name: buildUserName(userInfo),
    //   role,
    // })

    setUser({ id: userInfo.uuid, email: userInfo.email })

    setIsAuthenticating(false)
  }, [userInfo])

  useLayoutEffect(() => {
    const run = async () => {
      if (authCode) {
        setIsAuthenticating(true)
        await processAuthCode(authCode)
        setIsAuthenticating(false)
        return
      }

      const auth = getAuthInfo()
      if (auth) {
        setIsAuthenticating(true)
        const isVerified = await verifyAccessToken(
          auth.accessToken,
          (orgId) => {
            setIsNcte(orgId === NcteOrganization.id)
          },
        )
        if (isVerified) {
          if (pathname === "/") redirect()
        }
      } else {
        redirectToLogin()
      }

      setIsAuthenticating(false)
    }

    run()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const processAuthCode = async (code: string) => {
    try {
      const authData = await API.auth.tokens.generate({
        data: { code: code, platform: PLATFORM },
      })
      // Save auth in localStorage
      saveAuthInfo(authData)
      // Update userInfo
      setUserInfo(authData.user)

      redirect()
    } catch (error) {
      console.error("Failed to process auth code:", error)
    }

    // Clear code
    searchParams.delete("code")
    setSearchParams(searchParams, { replace: true })
  }

  const redirect = () => {
    navigate("/", {
      replace: true,
    })
  }

  const value = {
    isAuthenticated,
    setUserInfo,
    userInfo,
    isNcte,
  }

  if (isAuthenticating) {
    return (
      <FullPageLoading
        message={IS_LOCALHOST ? "Processing auth code..." : undefined}
      />
    )
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) throw new Error("useAuth must be used within AuthProvider")
  return context
}

const verifyAccessToken = async (
  token: string,
  callback?: (orgId: string | null) => void,
) => {
  try {
    const res = await API.auth.tokens.verify({
      data: {
        token: token,
      },
      headers: {
        Authorization: null,
      },
    })

    if (res.verified) {
      if (callback && res.organizationId) {
        callback(res.organizationId)
      }
      return true
    } else {
      await refreshToken()
      return true
    }
  } catch (e) {
    console.warn("> Access token is invalid", e)
    return false
  }
}
